import { Asset, Vec3 as PcVec3, type AppBase, type Entity } from 'playcanvas';
import { loadGsplat } from '../../submodules/supersplat-viewer/src/core/app';
import type { Config, Global } from '../../submodules/supersplat-viewer/src/types';
import type { App } from '../../submodules/supersplat-viewer/src/app';
import type { ViewerInitOptions, ViewerSceneOptions } from '@mindcloudxai/shared';
import { buildExperienceSettings, parseHexColor } from './settings';
import { applyEntityTransform } from './transform';
import { fromSettingsAnnotation } from './state';
import { loadSkyboxIfAny, loadCollisionIfAny } from './resource-loader';
import { loadSuperSplatViewerModule, type SplatHandle, type SuperSplatRuntime } from './types';
import { ENABLED_SHORTCUTS } from './constants';

export function buildViewerConfig(options: ViewerInitOptions): Config {
  return {
    noui: true,
    noanim: false,
    nofx: false,
    ministats: options.ministats ?? false,
    colorize: false,
    fullload: options.fullLoad ?? false,
    aa: options.aa ?? false,
    budget: options.splatBudget,
    renderer: options.renderer ?? 'webgpu',
    heatmap: false,
    debug: false,
    skipDefaultCollision: false,
    skipDefaultTrack: false,
    inputEnabled: options.inputEnabled ?? true,
    enabledShortcuts: ENABLED_SHORTCUTS,
    avatarUrl: options.avatarUrl,
    avatarEnabled: options.avatarEnabled ?? !!options.avatarUrl,
    avatarFollowCamera: options.avatarFollowCamera ?? true,
    avatarMapping: options.avatarMapping,
  };
}

export function buildViewerGlobal(
  app: App,
  camera: Entity,
  renderer: 'webgl' | 'webgpu',
  options: ViewerInitOptions,
  events: Global['events'],
  state: Global['state'],
): Global {
  const config = buildViewerConfig(options);
  return {
    app,
    settings: buildExperienceSettings(options, { url: '' }),
    config,
    state,
    events,
    camera,
    renderer,
  };
}

export function applyInitialCamera(camera: Entity, options: ViewerInitOptions): void {
  // headless 下直接在此挂载 camera component（standalone 在 app.start 后挂载）
  camera.addComponent('camera', {
    clearColor: parseHexColor(options.background ?? '#000000'),
    fov: options.camera?.fov ?? 65,
    nearClip: options.camera?.near ?? 0.01,
    farClip: options.camera?.far ?? 1000,
  });

  if (options.camera) {
    const cam = options.camera;
    camera.setPosition(cam.position.x, cam.position.y, cam.position.z);
    camera.lookAt(new PcVec3(cam.target.x, cam.target.y, cam.target.z));
  }
}

export interface LoadSceneResult {
  viewer: SuperSplatRuntime;
  splat: SplatHandle;
  annotations: ReturnType<typeof fromSettingsAnnotation>[];
}

export async function loadSceneContent(
  app: App,
  global: Global,
  initOptions: ViewerInitOptions,
  options: ViewerSceneOptions,
): Promise<LoadSceneResult> {
  const settings = buildExperienceSettings(initOptions, options);
  global.settings = settings;
  global.config.contentUrl = options.url;
  global.config.noanim = options.autoPlayAnim === false;
  global.config.skyboxUrl = options.skyboxUrl ?? settings.background.skyboxUrl;
  global.config.collisionUrl = options.collisionUrl;
  global.config.collisionBinUrl = options.collisionBinUrl;
  global.state.collisionOverlayEnabled = !!options.debugCollision;
  // 文件拖拽等无碰撞数据场景跳过默认平地 fallback
  global.config.skipDefaultCollision = !!options.skipDefaultCollision;
  global.config.skipDefaultTrack = !!options.skipDefaultTrack;

  const annotations = settings.annotations.map(fromSettingsAnnotation);

  // sogMeta 存在时用内联 JSON 构建 blob URL，避免额外网络下载
  let loadUrl = options.url;
  let filename: string;
  if (options.sogMeta) {
    const blob = new Blob([JSON.stringify(options.sogMeta)], { type: 'application/json' });
    loadUrl = URL.createObjectURL(blob);
    filename = 'scene.json';
  } else {
    // blob URL 无扩展名时用 options.filename 兜底，确保格式检测正确
    filename =
      options.filename ??
      new URL(options.url, location.href).pathname.split('/').pop() ??
      'scene.ply';
  }

  const gsplatLoad = loadGsplat(
    app,
    { url: loadUrl, filename, aa: global.config.aa },
    (p: number) => {
      global.state.progress = p;
    },
  );
  const skyboxLoad = loadSkyboxIfAny(app, global.config);
  const collisionLoad = loadCollisionIfAny(app, global.config);

  const SuperSplatModule = await loadSuperSplatViewerModule();
  const viewer = new SuperSplatModule(global, gsplatLoad, skyboxLoad, collisionLoad);

  const entity = await gsplatLoad;

  // sogMeta 产生的 blob URL 用完即释放
  if (options.sogMeta && loadUrl !== options.url) {
    URL.revokeObjectURL(loadUrl);
  }

  // entity 已带 gsplat loader 预置的 Rz(180°)（见 app.ts loadGsplat），
  // baseRotation 在此之上追加 up-axis 对齐（如 Z-up→Y-up 的 Rx(+90°)）；
  // collision 数据应与 splat 同坐标系，当前假定后端已对齐。
  applyEntityTransform(entity, options.baseRotation, options.transform);

  const splat = createSplatHandle(app, entity, options.url);

  return { viewer, splat, annotations };
}

export function createSplatHandle(app: AppBase, entity: Entity, sourceUrl: string): SplatHandle {
  return {
    entity,
    dispose() {
      const gsplat = entity.gsplat;
      if (gsplat) {
        // 通过 app.assets 统一查找再释放，避免 asset 引用类型不匹配
        const assetRef = gsplat.asset;
        const pcAsset =
          assetRef instanceof Asset
            ? assetRef
            : typeof assetRef === 'number'
              ? app.assets.get(assetRef)
              : undefined;
        if (pcAsset) {
          app.assets.remove(pcAsset);
          pcAsset.unload();
        }
      }
      entity.destroy();
      void sourceUrl;
    },
  };
}
